/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { string, bool } from 'prop-types';
import loadConfiguration from 'ggApp/utils/configuration';

import { Translator } from 'ggApp/modules/translator/components';
import {
    mixLinkOnNavEnabled as mixLinkOnNavEnabledSelector,
    isB2BStoreOpenEnabled as isB2BStoreOpenEnabledSelector,
} from 'ggApp/modules/featureFlag/selectors';
import { getHIWPageUrl } from 'ggApp/utils/path';
import { useDixa } from 'ggApp/modules/dixa/hooks';

import { Wrap, Link, Button } from './styled';

const { APP_CONFIG } = loadConfiguration();
const { shopHost: baseUrl } = APP_CONFIG;

export const utmParams = '?utm_source=grover&utm_medium=referral';

const PageLinks = ({
    className,
    isDesktopUser,
    isTransparent,
    isLoggedIn,
    countryCode,
    storeCode,
    activeLanguage,
    isBusinessStore,
    isPartner,
    businessStoreDealsEnabled,
    requestOfferEnabled,
    mixLinkOnNavEnabled,
    isB2BStoreOpenEnabled,
    b2cRedirectionModal,
    user,
}) => {
    const { show: showDixaMessenger } = useDixa(activeLanguage);
    let external = true;

    if (isPartner) {
        external = true;
    }

    const businessLinkOnClick = useCallback(
        (e) => {
            e.preventDefault();
            b2cRedirectionModal.openModal();
        },
        [b2cRedirectionModal],
    );

    const shouldShowRedirectionModal =
        isBusinessStore &&
        isLoggedIn &&
        user.default_store &&
        user.default_store.code.includes('business');

    const builStoreLinkProps = useCallback(() => {
        if (isBusinessStore) {
            return {
                id: 'customer-lp',
                tk: 'OFFERINGS_LINKS_B2C_TEXT',
                to: `/${countryCode}-${activeLanguage}`,
                external: true,
                target: '_self',
            };
        }
        const businessStore = countryCode === 'de' ? 'business' : `business_${countryCode}`;
        const hasLandingPage = countryCode === 'de' || isB2BStoreOpenEnabled;
        const landingPageLink = `${baseUrl}/${businessStore}-${activeLanguage}/for-business`;
        return {
            id: 'business-lp',
            tk: 'OFFERINGS_LINKS_B2B_TEXT',
            to: hasLandingPage
                ? landingPageLink
                : `https://get-business.grover.com/${countryCode}-${activeLanguage}/${utmParams}`,
            external: hasLandingPage,
            target: hasLandingPage ? '_self' : '_blank',
        };
    }, [isBusinessStore, countryCode, isB2BStoreOpenEnabled, activeLanguage]);

    const storeLink = useCallback(() => {
        if (!isDesktopUser) {
            return null;
        }

        const linkProps = builStoreLinkProps();
        return (
            <Link
                id={linkProps.id}
                pageLinkTo={linkProps.to}
                pageLinkTk={linkProps.tk}
                external={linkProps.external}
                type="countrySpecificLink"
                target={linkProps.target}
                onClick={shouldShowRedirectionModal ? businessLinkOnClick : undefined}
            />
        );
    }, [isDesktopUser, builStoreLinkProps, shouldShowRedirectionModal, businessLinkOnClick]);

    return (
        <Wrap className={className} isTransparent={isTransparent}>
            {(isDesktopUser || !isLoggedIn) && (
                <Link
                    pageLinkTo={getHIWPageUrl(storeCode, activeLanguage)}
                    pageLinkTk="TIPS_LINKS_HIW_TEXT"
                    external={external}
                    type="countrySpecificLink"
                />
            )}

            {(isDesktopUser || isLoggedIn) && (
                <Link pageLinkTo="/trending" pageLinkTk="OFFERINGS_LINKS_TOP_PRODUCTS_TEXT" />
            )}

            {isDesktopUser && businessStoreDealsEnabled && (
                <Link pageLinkTo="/deals" pageLinkTk="OFFERINGS_LINKS_DEALS_TEXT" />
            )}

            {mixLinkOnNavEnabled && isDesktopUser && !isBusinessStore && storeCode === 'de' && (
                <Link pageLinkTo="/mix" pageLinkTk="MIX_LANDING_NAV_TITLE" />
            )}

            {isDesktopUser && requestOfferEnabled && (
                <Button onClick={showDixaMessenger} type="button">
                    <Translator tk="OFFERINGS_LINKS_REQUEST_OFFER_TEXT" />
                </Button>
            )}

            {storeLink()}
        </Wrap>
    );
};

PageLinks.propTypes = {
    className: string,
    isDesktopUser: bool,
    isLoggedIn: bool,
    countryCode: string,
    storeCode: string,
    activeLanguage: string,
    isBusinessStore: bool,
    isPartner: bool,
    businessStoreDealsEnabled: bool,
    requestOfferEnabled: bool,
    mixLinkOnNavEnabled: bool,
};

PageLinks.defaultProps = {
    className: '',
    isDesktopUser: false,
    isLoggedIn: false,
    countryCode: '',
    storeCode: '',
    activeLanguage: '',
    isBusinessStore: false,
    isPartner: false,
    requestOfferEnabled: false,
    businessStoreDealsEnabled: false,
    mixLinkOnNavEnabled: false,
};

const enhance = connect(
    createStructuredSelector({
        mixLinkOnNavEnabled: mixLinkOnNavEnabledSelector,
        isB2BStoreOpenEnabled: isB2BStoreOpenEnabledSelector,
    }),
);

export default enhance(PageLinks);
